import { useAuth0 } from '@auth0/auth0-react';
import {
  useGridLayout,
  useGridViews,
  useOrganisation,
  useParties,
  useLocations,
} from '@my/directory-api/hooks';
import type { Address, GridColumnDef, Party } from '@my/directory-api';
import {
  useCargoDefinitions,
  useOrdersEmissions,
  useOrdersPage,
  useServiceTags,
  orderTaskKeys,
} from '@my/tms-api/hooks';
import { tmsApi } from '@my/tms-api';
import type {
  CargoDefinition,
  Order,
  OrderEmissions,
  OrderStatusValue,
  OrderTask,
  ServiceTag,
} from '@my/tms-api';
import {
  ColumnChooser,
  ColumnResizeHandle,
  columnWidthStyle,
  ErrorBanner,
  GridViewMenu,
  Loading,
  sizedCell,
} from '@my/ui';
import { useQueries } from '@tanstack/react-query';
import {
  AlertTriangle,
  BookmarkPlus,
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
  Pencil,
  Plus,
  Search,
} from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  useOrdersViewStore,
  type OrdersSortKey,
  type OrdersStatusFilter,
} from '@/stores/ordersView';
import { RowActionsMenu, type RowAction } from '@/components/RowActionsMenu';
import { ORDER_STATUS_DOT, ORDER_STATUS_LABEL } from './lib/orderStatus';
import { OrderStatusPill } from './components/OrderStatusPill';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import {
  extractOrderReferenceValues,
  parseOrderReferenceLabels,
  parseRequiredOrderReferenceSlots,
  resolveOrderReferenceLabel,
} from '@/utils/orderReferenceLabels';
import { CargoCell, CargoInfoCell, EndpointCell, RouteStrip } from './components/OrderListCells';
import { nextPointTask } from './lib/orderPoints';
import { CopyOrderDialog } from './components/CopyOrderButton';
import { SaveAsTemplateDialog } from './components/OrderTemplateDialogs';
import { parseCopyFunctions } from './lib/copyFunctions';

// ────────────────────────────────────────────────────────────────────────────
// OrdersPage — list view, wired to the TMS BE.
//
// Column set mirrors the design mock: Order (+ status pill), References,
// Customer, Cargo (+ service-tag chips), Start point, End point, and the
// Route strip. Start / End / Route are derived from each order's *tasks*
// (resolved against the org's address book), which the orders-list
// endpoint doesn't embed — so we fan out one `getOrderTasks` query per
// row via `useQueries`. They share the same cache key the Order Details
// page uses, so drilling in is warm.
//
// Visual pattern matches `PartyListPage` (filter chips left, search +
// "+Add" right, sortable table with sticky-right Actions). The rows come
// 20 at a time as the grid scrolls (`useOrdersPage`): search, status and
// sort run on the server so the order holds across pages, and the per-row
// task fan-out stays at the rows actually loaded.
// ────────────────────────────────────────────────────────────────────────────

/**
 * The grid's columns, for the Columns chooser (`useGridLayout`). The ids are
 * what saved layouts store — never rename one without mapping the old id. A
 * new column added here appears for everyone per its `defaultVisible`, after
 * the column it follows, with no layout needing a reset.
 */
const ORDERS_COLUMNS = [
  { id: 'order', label: 'Order', locked: true },
  { id: 'references', label: 'References' },
  { id: 'customer', label: 'Customer' },
  { id: 'cargo', label: 'Cargo' },
  { id: 'info', label: 'Info' },
  { id: 'start', label: 'Start point' },
  // The first task still to do (Pending or In progress), in sequence.
  { id: 'next', label: 'Next point' },
  { id: 'end', label: 'End point' },
  { id: 'route', label: 'Route' },
  // Off until someone ticks them in Columns.
  { id: 'created', label: 'Created', defaultVisible: false },
  { id: 'invoiceTo', label: 'Invoice to', defaultVisible: false },
  { id: 'temperature', label: 'Temperature', defaultVisible: false },
  { id: 'stops', label: 'Stops', defaultVisible: false },
  { id: 'co2', label: 'CO₂', defaultVisible: false },
  { id: 'notes', label: 'Notes', defaultVisible: false },
] as const satisfies readonly GridColumnDef[];
type OrdersColumnId = (typeof ORDERS_COLUMNS)[number]['id'];
const ORDERS_COLUMN_DEFS: GridColumnDef[] = [...ORDERS_COLUMNS];
const ORDERS_COLUMN_LABEL = new Map<string, string>(ORDERS_COLUMNS.map((c) => [c.id, c.label]));
/** Body cells' horizontal padding (px-3 both sides) — a sized cell's content is that much narrower. */
const CELL_PADDING = 24;
const RIGHT_ALIGNED = new Set<string>(['stops', 'co2']);
const NO_IDS: string[] = [];

type SortKey = OrdersSortKey;

/** The Sort dropdown, in the order it lists them. `null` is the default, newest first. */
const SORT_OPTIONS: { key: SortKey | null; label: string }[] = [
  { key: null, label: 'Newest' },
  { key: 'orderNumber', label: 'Order number' },
  { key: 'customer', label: 'Customer' },
  { key: 'startPoint', label: 'Start point date/time' },
  { key: 'nextPoint', label: 'Next point date/time' },
];
const isSortKey = (v: unknown): v is SortKey =>
  SORT_OPTIONS.some((o) => o.key !== null && o.key === v);
type StatusFilter = OrdersStatusFilter;

/**
 * What "All" means: the orders still being worked. Completed and Cancelled
 * pile up for ever, so they sit apart on chips of their own — and only those
 * two get a date range, the way the invoice list bounds its closed invoices.
 */
const ACTIVE_STATUSES: OrderStatusValue[] = ['Draft', 'Open', 'InProgress'];
const CLOSED_STATUSES: OrderStatusValue[] = ['Completed', 'Cancelled'];
const isClosedStatus = (s: StatusFilter): boolean => s !== 'all' && CLOSED_STATUSES.includes(s);

/** This calendar month, as `yyyy-MM-dd` — the date range's starting window. */
function defaultRange(): { from: string; to: string } {
  const pad = (n: number) => String(n).padStart(2, '0');
  const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const now = new Date();
  return {
    from: iso(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
}

export default function OrdersPage() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const navigate = useNavigate();

  // Filters and sort live in a persisted store, so opening an order and coming
  // back (or a refresh) keeps the list as it was.
  const search = useOrdersViewStore((s) => s.search);
  const setSearch = useOrdersViewStore((s) => s.setSearch);
  const filterStatus = useOrdersViewStore((s) => s.status);
  const setFilterStatus = useOrdersViewStore((s) => s.setStatus);
  const sortKey = useOrdersViewStore((s) => s.sortKey);
  const setSortKey = useOrdersViewStore((s) => s.setSortKey);
  const sortDir = useOrdersViewStore((s) => s.sortDir);
  const setSortDir = useOrdersViewStore((s) => s.setSortDir);
  // Only applies — and only shows — while Completed or Cancelled is picked.
  // An untouched bound is this month, worked out now.
  const storedFrom = useOrdersViewStore((s) => s.from);
  const storedTo = useOrdersViewStore((s) => s.to);
  const setFrom = useOrdersViewStore((s) => s.setFrom);
  const setTo = useOrdersViewStore((s) => s.setTo);
  const from = storedFrom ?? defaultRange().from;
  const to = storedTo ?? defaultRange().to;
  const dateBounded = isClosedStatus(filterStatus);

  // The server searches, so wait for a pause in typing rather than asking on
  // every key. Starts at the remembered search so coming back doesn't load
  // an unsearched page first.
  const [debouncedSearch, setDebouncedSearch] = useState(() => search.trim());
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Which columns show, and in what order: the organisation default with the
  // user's own layout over it (the Columns button).
  const layout = useGridLayout(orgId, 'tms', 'orders', ORDERS_COLUMN_DEFS);
  const columnIds = layout.visibleColumnIds as OrdersColumnId[];

  // Saved views (the Views button): columns only. The filters and sort are
  // the store's above, so picking a view never changes them.
  const views = useGridViews({
    organisationId: orgId,
    app: 'tms',
    gridKey: 'orders',
    layout,
  });

  // Lookups that hydrate the list:
  //   1. Orders — the rows themselves, a page at a time.
  //   2. Customers (Parties of type Customer) — resolve `customerPartyId`
  //      to a name + account code.
  //   3. Cargo definitions — resolve `cargoDefinitionId` → display name
  //      (and the variant name for IsoTank / Container rows).
  //   4. Service tags — resolve `serviceTagIds` → chip labels.
  //   5. Locations — org-wide address book; resolves each task's
  //      `addressId` → name / locality / country for Start / End / Route.
  const ordersQuery = useOrdersPage(
    orgId
      ? {
          organisationId: orgId,
          search: debouncedSearch,
          status: filterStatus === 'all' ? null : filterStatus,
          activeOnly: filterStatus === 'all',
          from: dateBounded && from ? from : null,
          to: dateBounded && to ? to : null,
          sort: sortKey ?? 'newest',
          descending: sortDir === 'desc',
        }
      : null,
  );
  const customersQuery = useParties(orgId, 'Customer');
  const cargoDefsQuery = useCargoDefinitions(orgId);
  const serviceTagsQuery = useServiceTags(orgId);
  const locationsQuery = useLocations(orgId);
  // Only for the reference LABELS — a missing required reference is named the
  // way the operator named the field, not "Reference 3". Already cached by the
  // pages that read the organisation, so this costs no round-trip in practice.
  const organisationQuery = useOrganisation(orgId);

  const orders = useMemo(() => ordersQuery.data?.orders ?? [], [ordersQuery.data]);
  // CO₂ only when its column is showing — reading it can replan a journey.
  const orderIds = useMemo(() => orders.map((o) => o.id), [orders]);
  const emissionsByOrderId = useOrdersEmissions(
    orgId,
    columnIds.includes('co2') ? orderIds : NO_IDS,
  );

  // Row menu → Copy: the same copy dialog as Order Details, when the
  // organisation has the copy feature (Copy Functions, shown by default).
  const copyFunctions = parseCopyFunctions(organisationQuery.data?.tmsCopyFunctionsJson);
  const canCopy = copyFunctions.ordersCopySection;
  const [copying, setCopying] = useState<Order | null>(null);
  // Row menu → Save as template (Order templates on in Copy Functions).
  const [templating, setTemplating] = useState<{ order: Order; suggestedName: string } | null>(
    null,
  );

  const orderReferenceLabels = useMemo(
    () => parseOrderReferenceLabels(organisationQuery.data?.tmsOrderReferenceLabelsJson),
    [organisationQuery.data?.tmsOrderReferenceLabelsJson],
  );

  // One tasks query per order. Same key + queryFn as `useOrderTasks` so the
  // cache is shared with the Order Details page. The browser caps concurrent
  // requests per host, so a large list queues rather than stampedes.
  const taskQueries = useQueries({
    queries: orders.map((o) => ({
      queryKey: orderTaskKeys.byOrder(o.id),
      queryFn: () => tmsApi.getOrderTasks(o.id),
      enabled: !!orgId,
      staleTime: 30 * 1000,
    })),
  });

  /** The grab strip on a header's right edge: drag to size, double-click to fit. */
  const resizerFor = (id: string) => (
    <ColumnResizeHandle
      onResize={(w) => layout.previewWidth(id, w)}
      onResizeEnd={(w) => (w == null ? layout.previewWidth(id, null) : layout.setWidth(id, w))}
      onReset={() => layout.setWidth(id, null)}
    />
  );

  const hasActiveFilters = search.trim() !== '' || filterStatus !== 'all';
  const clearFilters = () => {
    setSearch('');
    setFilterStatus('all');
    setFrom(null);
    setTo(null);
  };

  // Customer lookup — `partyId → Party`.
  const customersById = useMemo(() => {
    const map = new Map<string, Party>();
    for (const p of customersQuery.data ?? []) map.set(p.id, p);
    return map;
  }, [customersQuery.data]);

  // Cargo lookup — `defId → CargoDefinition`.
  const cargoDefsById = useMemo(() => {
    const map = new Map<string, CargoDefinition>();
    for (const d of cargoDefsQuery.data ?? []) map.set(d.id, d);
    return map;
  }, [cargoDefsQuery.data]);

  // Service-tag lookup — `tagId → ServiceTag`.
  const serviceTagsById = useMemo(() => {
    const map = new Map<string, ServiceTag>();
    for (const t of serviceTagsQuery.data ?? []) map.set(t.id, t);
    return map;
  }, [serviceTagsQuery.data]);

  // Address lookup — `addressId → Address`.
  const addressById = useMemo(() => {
    const map = new Map<string, Address>();
    for (const a of locationsQuery.data ?? []) map.set(a.id, a);
    return map;
  }, [locationsQuery.data]);

  // Per-order task state, keyed by order id. Built each render (cheap) since
  // `useQueries` hands back a fresh array; tasks are sorted by `sequence` so
  // Start / End / Route read left-to-right in execution order.
  const taskStateByOrderId = new Map<string, { tasks: OrderTask[]; isPending: boolean }>();
  orders.forEach((o, i) => {
    const q = taskQueries[i];
    const tasks = (q?.data ?? []).slice().sort((a, b) => a.sequence - b.sequence);
    taskStateByOrderId.set(o.id, { tasks, isPending: q?.isPending ?? true });
  });

  // Already searched, filtered and sorted by the server.
  const visibleOrders = orders;
  const countByStatus = ordersQuery.data?.countByStatus ?? new Map<OrderStatusValue, number>();
  // "All" counts what it lists: the active orders, not the organisation's total.
  const activeCount = ACTIVE_STATUSES.reduce((sum, s) => sum + (countByStatus.get(s) ?? 0), 0);
  const matchingCount = ordersQuery.data?.totalCount ?? 0;

  // The next 20 load when the operator scrolls near the end of the grid.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = ordersQuery;
  const onGridScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const box = e.currentTarget;
    if (!hasNextPage || isFetchingNextPage) return;
    if (box.scrollTop + box.clientHeight >= box.scrollHeight - 200) void fetchNextPage();
  };

  // A tall screen can hold more than 20 rows; keep loading until the grid
  // overflows, or there is nothing left — otherwise there is no scrollbar to
  // scroll. Only once the loaded rows have their tasks: a row still waiting is
  // shorter than it will be, and measuring then would over-fetch.
  const gridRef = useRef<HTMLDivElement | null>(null);
  const rowsSettled = taskQueries.every((q) => !q.isPending);
  useEffect(() => {
    const box = gridRef.current;
    if (!box || !hasNextPage || isFetchingNextPage || !rowsSettled) return;
    if (box.scrollHeight <= box.clientHeight + 1) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, rowsSettled, orders.length, fetchNextPage]);

  const statusChip = (s: OrderStatusValue) => {
    // Completed and Cancelled are counted within the date range, so only the
    // picked one has a number — the server's count of what it is listing. Not
    // while the previous filter's rows are still standing in, or the chip
    // would briefly show the last chip's total.
    const count = !CLOSED_STATUSES.includes(s)
      ? (countByStatus.get(s) ?? 0)
      : filterStatus === s && !ordersQuery.isPlaceholderData
        ? matchingCount
        : 0;
    return (
      <button
        key={s}
        type="button"
        onClick={() => setFilterStatus(s)}
        className={`inline-flex items-center gap-1 ${chipClass(filterStatus === s)}`}
      >
        <span className={`h-1.5 w-1.5 rounded-full ${ORDER_STATUS_DOT[s]}`} aria-hidden="true" />
        {ORDER_STATUS_LABEL[s]}
        {count > 0 && <span className="text-[10px] opacity-60">({count})</span>}
      </button>
    );
  };

  const startNew = () => navigate('/orders/new');
  const openOrder = (id: string) => navigate(`/orders/${id}`);

  return (
    // The page fills its shell and does NOT scroll: the grid below owns the
    // scrolling, in both directions. Scrolling here instead is what put the
    // horizontal scrollbar at the foot of a 500-row table, where reaching it
    // meant scrolling past every row first.
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex min-h-0 flex-1 flex-col rounded-t-lg border border-slate-200 bg-white pt-1">
        {/* No bottom padding: the grid now runs to the foot of the card, so
            anything here is dead space under the horizontal scrollbar rather
            than breathing room under the last row. */}
        <main className="flex min-h-0 flex-1 flex-col gap-3">
          {!orgId && (
            <ErrorBanner
              title="No organisation linked to this account"
              message="Your access token does not include a TMS organisation claim. Contact an administrator."
            />
          )}

          {orgId && ordersQuery.isPending && <Loading label="Orders" />}

          {orgId && ordersQuery.isError && (
            <ErrorBanner title="Failed to load orders" message={ordersQuery.error.message} />
          )}

          {orgId && ordersQuery.isSuccess && (
            <>
              {/* ── Filters + search + add button ────────────────────────── */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setFilterStatus('all')}
                  className={`inline-flex items-center gap-1 ${chipClass(filterStatus === 'all')}`}
                >
                  All
                  <span className="text-[10px] opacity-60">({activeCount})</span>
                </button>
                {ACTIVE_STATUSES.map(statusChip)}
                <div className="mx-1 h-5 w-px bg-slate-200" aria-hidden="true" />
                {CLOSED_STATUSES.map(statusChip)}

                {dateBounded && (
                  <div className="flex items-center gap-1 text-[11px] text-slate-500">
                    <input
                      type="date"
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                      aria-label="From date"
                      className="rounded-md border border-slate-300 bg-white px-1.5 py-1 text-[11px] text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                    />
                    <span>–</span>
                    <input
                      type="date"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      aria-label="To date"
                      className="rounded-md border border-slate-300 bg-white px-1.5 py-1 text-[11px] text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                    />
                    {(from || to) && (
                      <button
                        type="button"
                        onClick={() => {
                          setFrom('');
                          setTo('');
                        }}
                        className="ml-0.5 rounded px-1 text-slate-400 hover:text-slate-700"
                        title="Clear dates"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                )}

                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="text-[10px] text-slate-400 underline hover:text-slate-600"
                  >
                    Clear
                  </button>
                )}

                <div className="flex-1" />

                <div className="flex items-center gap-1 text-[11px] text-slate-500">
                  {/* Labelled like the Plan bar's fields (Group, Cargo, Direction). */}
                  <label
                    htmlFor="orders-sort"
                    className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400"
                  >
                    Sort
                  </label>
                  <select
                    id="orders-sort"
                    value={sortKey ?? ''}
                    onChange={(e) => setSortKey(isSortKey(e.target.value) ? e.target.value : null)}
                    className="rounded-md border border-slate-300 bg-white px-1.5 py-1 text-[11px] text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    {SORT_OPTIONS.map((o) => (
                      <option key={o.key ?? 'newest'} value={o.key ?? ''}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {/* Newest is always newest first, so it has no direction. */}
                  <button
                    type="button"
                    disabled={!sortKey}
                    onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
                    title={
                      sortDir === 'asc'
                        ? 'Ascending — click for descending'
                        : 'Descending — click for ascending'
                    }
                    aria-label={sortDir === 'asc' ? 'Sort ascending' : 'Sort descending'}
                    className="inline-flex items-center gap-0.5 rounded-md border border-slate-300 bg-white px-1.5 py-1 text-[11px] text-slate-700 hover:border-slate-500 disabled:cursor-default disabled:opacity-40"
                  >
                    {sortDir === 'asc' ? (
                      <ChevronUp className="h-3 w-3" aria-hidden="true" />
                    ) : (
                      <ChevronDown className="h-3 w-3" aria-hidden="true" />
                    )}
                    {sortDir === 'asc' ? 'Asc' : 'Desc'}
                  </button>
                </div>

                <div className="relative">
                  <Search
                    className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                    aria-hidden="true"
                  />
                  <input
                    type="text"
                    placeholder="Search orders…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-52 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>

                <GridViewMenu
                  views={views.views}
                  selectedId={views.selected?.id ?? null}
                  dirty={views.dirty}
                  canShare={views.canShare}
                  onSelect={views.select}
                  onClear={views.clear}
                  onSaveNew={views.saveAsNew}
                  onUpdate={views.updateSelected}
                  onRename={views.rename}
                  onDelete={views.remove}
                  busy={views.busy}
                  error={views.error}
                />

                <ColumnChooser
                  columns={layout.columns}
                  onChange={layout.setColumns}
                  hasOwnLayout={layout.hasOwnLayout}
                  onResetMine={() => void layout.resetMine()}
                  hasOrganisationLayout={layout.hasOrganisationLayout}
                  canEditOrganisationDefault={layout.canEditOrganisationDefault}
                  onSaveOrganisationDefault={() => void layout.saveAsOrganisationDefault()}
                  onResetOrganisationDefault={() => void layout.resetOrganisationDefault()}
                  onResetWidths={layout.resetWidths}
                  busy={layout.busy}
                  error={layout.saveError}
                />

                <button
                  type="button"
                  onClick={startNew}
                  className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add
                </button>
              </div>

              {/* ── Table ─────────────────────────────────────────────────── */}
              {/* The grid's viewport. `min-h-0` is what lets a flex child be
                  SHORTER than its content — without it this box grows to the
                  table's full height and the scrollbars go with it. */}
              <div
                ref={gridRef}
                onScroll={onGridScroll}
                className={`min-h-0 flex-1 overflow-auto rounded-md transition-opacity ${
                  ordersQuery.isPlaceholderData ? 'opacity-60' : ''
                }`}
              >
                <div className="min-w-[1100px]">
                  <table className="w-full table-auto text-xs">
                    {/* Sticky within the scroll box above. Worth having on
                        its own: 500 rows is a long way to scroll with no
                        reminder of which column is which. */}
                    <thead className="sticky top-0 z-20 bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                      <tr>
                        {columnIds.map((id) => (
                          <Fragment key={id}>
                            <Th
                              align={RIGHT_ALIGNED.has(id) ? 'right' : undefined}
                              width={layout.widthOf(id)}
                              resizer={resizerFor(id)}
                            >
                              {ORDERS_COLUMN_LABEL.get(id)}
                            </Th>
                          </Fragment>
                        ))}
                        <th className="sticky right-0 top-0 z-30 whitespace-nowrap bg-slate-700 px-3 py-2 text-right font-semibold">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-500 bg-white">
                      {visibleOrders.length === 0 ? (
                        <tr>
                          <td
                            colSpan={columnIds.length + 1}
                            className="px-3 py-6 text-center text-xs italic text-slate-400"
                          >
                            {hasActiveFilters
                              ? 'No orders match the selected filters.'
                              : 'No orders yet — click Add to create the first one.'}
                          </td>
                        </tr>
                      ) : (
                        visibleOrders.map((o) => {
                          const ts = taskStateByOrderId.get(o.id);
                          return (
                            <OrderRow
                              key={o.id}
                              order={o}
                              columnIds={columnIds}
                              widthOf={layout.widthOf}
                              customer={customersById.get(o.customerPartyId)}
                              invoiceTo={
                                o.invoiceToPartyId
                                  ? customersById.get(o.invoiceToPartyId)
                                  : undefined
                              }
                              emissions={emissionsByOrderId.get(o.id)}
                              cargoDef={cargoDefsById.get(o.cargoDefinitionId)}
                              serviceTagsById={serviceTagsById}
                              addressById={addressById}
                              tasks={ts?.tasks ?? []}
                              tasksPending={ts?.isPending ?? true}
                              referenceLabels={orderReferenceLabels}
                              onOpen={() => openOrder(o.id)}
                              onCopy={canCopy ? () => setCopying(o) : undefined}
                              onSaveAsTemplate={
                                copyFunctions.ordersTemplates
                                  ? (suggestedName) => setTemplating({ order: o, suggestedName })
                                  : undefined
                              }
                            />
                          );
                        })
                      )}
                      {visibleOrders.length > 0 && (isFetchingNextPage || !hasNextPage) && (
                        <tr>
                          <td
                            colSpan={columnIds.length + 1}
                            className="px-3 py-2 text-center text-[11px] italic text-slate-400"
                          >
                            {isFetchingNextPage
                              ? 'Loading more orders…'
                              : `All ${matchingCount.toLocaleString()} ${
                                  matchingCount === 1 ? 'order' : 'orders'
                                } shown`}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </main>
      </div>

      {/* One dialog for the page; the row menu says which order. */}
      {templating && (
        <SaveAsTemplateDialog
          orderId={templating.order.id}
          orderNumber={templating.order.orderNumber}
          suggestedName={templating.suggestedName}
          open
          onClose={() => setTemplating(null)}
        />
      )}
      <CopyOrderDialog
        order={copying}
        organisationId={orgId}
        open={copying !== null}
        onClose={() => setCopying(null)}
      />
    </div>
  );
}

// ── Row + cells ─────────────────────────────────────────────────────────────

interface OrderRowProps {
  order: Order;
  /** The visible columns, in order. */
  columnIds: OrdersColumnId[];
  /** A sized column's width; undefined = fits its content. */
  widthOf: (id: string) => number | undefined;
  /** The invoice-to party when it isn't the customer. */
  invoiceTo: Party | undefined;
  /** Stored CO₂ estimate — only read while the CO₂ column shows. */
  emissions: OrderEmissions | undefined;
  customer: Party | undefined;
  cargoDef: CargoDefinition | undefined;
  serviceTagsById: Map<string, ServiceTag>;
  addressById: Map<string, Address>;
  tasks: OrderTask[];
  tasksPending: boolean;
  /** Positional org labels — index `i` names `Reference${i + 1}`. */
  referenceLabels: string[];
  onOpen: () => void;
  /** Opens the copy dialog for this order; absent when the organisation has no copy. */
  onCopy?: () => void;
  /** Opens "Save as template" with a suggested name; absent when templates are off. */
  onSaveAsTemplate?: (suggestedName: string) => void;
}

function OrderRow({
  order,
  columnIds,
  widthOf,
  invoiceTo,
  emissions,
  customer,
  cargoDef,
  serviceTagsById,
  addressById,
  tasks,
  tasksPending,
  referenceLabels,
  onOpen,
  onCopy,
  onSaveAsTemplate,
}: OrderRowProps) {
  const startTask = tasks[0];
  const endTask = tasks.length > 1 ? tasks[tasks.length - 1] : undefined;
  // Where the job goes next: the first task not yet completed or cancelled.
  const nextTask = nextPointTask(tasks);

  /**
   * What this order can do from the list.
   *
   * <p>
   * Built per row rather than shared, because the useful actions depend on the
   * order's own state — a cancelled order has nothing to plan, an invoiced one
   * nothing to price. Only the two that need no new plumbing are here so far;
   * the state-dependent ones slot in as they arrive, and
   * `RowActionsMenu` shows a disabled item with its reason rather than hiding
   * it, so the menu keeps the same shape row to row.
   * </p>
   */
  const actions = useMemo<RowAction[]>(
    () => [
      {
        id: 'open',
        label: 'Open order',
        icon: <Pencil className="h-3.5 w-3.5" />,
        onSelect: onOpen,
      },
      {
        id: 'open-tab',
        label: 'Open in new tab',
        icon: <ExternalLink className="h-3.5 w-3.5" />,
        // A dispatcher comparing two orders should not have to lose the list
        // and its filters to do it.
        onSelect: () => window.open(`/orders/${order.id}`, '_blank', 'noopener'),
      },
      // A new order from this one — the same dialog as Order Details → Copy.
      ...(onCopy
        ? [
            {
              id: 'copy-order',
              label: `Copy ${order.orderNumber}`,
              icon: <Copy className="h-3.5 w-3.5" />,
              onSelect: onCopy,
            },
          ]
        : []),
      ...(onSaveAsTemplate
        ? [
            {
              id: 'save-template',
              label: 'Save as template',
              icon: <BookmarkPlus className="h-3.5 w-3.5" />,
              // "Carbery Group → Dublin Castle · Full Load"
              onSelect: () =>
                onSaveAsTemplate(
                  [
                    [customer?.name, endTask ? addressById.get(endTask.addressId)?.name : null]
                      .filter(Boolean)
                      .join(' → '),
                    cargoDef?.displayName,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                    .slice(0, 128),
                ),
            },
          ]
        : []),
    ],
    [
      order.id,
      order.orderNumber,
      onOpen,
      onCopy,
      onSaveAsTemplate,
      customer,
      endTask,
      addressById,
      cargoDef,
    ],
  );

  /** One cell of this row — the grid's visible columns pick which, in their order. */
  const cell = (id: OrdersColumnId) => {
    switch (id) {
      // Order + status pill
      case 'order':
        return (
          <td className="whitespace-nowrap px-3 py-2.5 align-top">
            <div className="space-y-1">
              <span className="text-xs font-semibold text-slate-900">{order.orderNumber}</span>
              <div>
                <OrderStatusPill status={order.status} />
              </div>
            </div>
          </td>
        );
      case 'references':
        return (
          <td className="whitespace-nowrap px-3 py-2.5 align-top">
            <ReferencesCell order={order} customer={customer} labels={referenceLabels} />
          </td>
        );
      case 'customer':
        return (
          <td className="whitespace-nowrap px-3 py-2.5 align-top">
            {customer ? (
              <div className="space-y-0.5">
                <div className="text-xs font-semibold text-slate-900">{customer.name}</div>
                <div className="text-[11px] text-slate-500">{customer.code}</div>
              </div>
            ) : (
              <span className="font-mono text-[11px] italic text-slate-400">
                {order.customerPartyId.slice(0, 8)}…
              </span>
            )}
          </td>
        );
      // Cargo + service-tag chips
      case 'cargo':
        return (
          <td className="px-3 py-2.5 align-top">
            <CargoCell order={order} cargoDef={cargoDef} serviceTagsById={serviceTagsById} />
          </td>
        );
      // Info — the identifying detail, whichever kind this cargo has
      case 'info':
        return (
          <td className="px-3 py-2.5 align-top">
            <CargoInfoCell order={order} cargoDef={cargoDef} tasks={tasks} pending={tasksPending} />
          </td>
        );
      case 'start':
        return (
          <td className="whitespace-nowrap px-3 py-2.5 align-top">
            <EndpointCell
              task={startTask}
              address={startTask ? addressById.get(startTask.addressId) : undefined}
              pending={tasksPending}
            />
          </td>
        );
      case 'next':
        return (
          <td className="whitespace-nowrap px-3 py-2.5 align-top">
            {!nextTask && !tasksPending && tasks.length > 0 ? (
              <span className="text-[11px] italic text-slate-400">All tasks done</span>
            ) : (
              <div title={nextTask?.state === 'InProgress' ? 'In progress' : undefined}>
                <EndpointCell
                  task={nextTask}
                  address={nextTask ? addressById.get(nextTask.addressId) : undefined}
                  pending={tasksPending}
                />
              </div>
            )}
          </td>
        );
      case 'end':
        return (
          <td className="whitespace-nowrap px-3 py-2.5 align-top">
            <EndpointCell
              task={endTask}
              address={endTask ? addressById.get(endTask.addressId) : undefined}
              pending={tasksPending}
            />
          </td>
        );
      case 'route':
        return (
          <td className="px-3 py-2.5 align-top">
            <RouteStrip tasks={tasks} addressById={addressById} pending={tasksPending} />
          </td>
        );
      case 'created':
        return (
          <td className="whitespace-nowrap px-3 py-2.5 align-top text-[11px] text-slate-600">
            {formatCreated(order.createdUtc)}
          </td>
        );
      case 'invoiceTo':
        return (
          <td className="whitespace-nowrap px-3 py-2.5 align-top">
            {!order.invoiceToPartyId ? (
              <span className="text-[11px] text-slate-400" title="Invoiced to the customer">
                —
              </span>
            ) : invoiceTo ? (
              <div className="space-y-0.5">
                <div className="text-xs text-slate-800">{invoiceTo.name}</div>
                <div className="text-[11px] text-slate-500">{invoiceTo.code}</div>
              </div>
            ) : (
              <span className="font-mono text-[11px] italic text-slate-400">
                {order.invoiceToPartyId.slice(0, 8)}…
              </span>
            )}
          </td>
        );
      case 'temperature':
        return (
          <td className="whitespace-nowrap px-3 py-2.5 align-top text-xs text-slate-700">
            {order.temperatureC == null ? (
              <span className="text-slate-400">—</span>
            ) : (
              `${order.temperatureC}°C`
            )}
          </td>
        );
      case 'stops':
        return (
          <td className="whitespace-nowrap px-3 py-2.5 text-right align-top text-xs text-slate-700">
            {tasksPending ? <span className="text-slate-300">…</span> : tasks.length}
          </td>
        );
      case 'co2':
        return (
          <td
            className="whitespace-nowrap px-3 py-2.5 text-right align-top text-xs text-slate-700"
            title={
              emissions
                ? `Est. laden CO₂ · ${Math.round(emissions.ladenKm).toLocaleString()} km laden${
                    emissions.complete ? '' : ' · partly estimated'
                  }`
                : undefined
            }
          >
            {emissions ? (
              `${emissions.complete ? '' : '~'}${Math.round(emissions.co2Kg).toLocaleString()} kg`
            ) : (
              <span className="text-slate-400">—</span>
            )}
          </td>
        );
      case 'notes':
        return (
          <td className="px-3 py-2.5 align-top">
            <div
              className="max-w-[260px] truncate text-[11px] text-slate-600"
              title={order.notes ?? undefined}
            >
              {order.notes?.trim() || <span className="text-slate-400">—</span>}
            </div>
          </td>
        );
    }
  };

  return (
    // Double-click, not click: every other grid in the app opens a record that
    // way (plan, manifest, for-approval, parties, resources), and a list where
    // a stray click navigates away is one where nobody can select a reference
    // to copy. The title says so, because a row that looks clickable and
    // ignores a click is otherwise a dead end.
    <tr
      className="group cursor-pointer align-top hover:bg-slate-50"
      onDoubleClick={onOpen}
      title="Double-click to open"
    >
      {columnIds.map((id) => (
        <Fragment key={id}>{sizedCell(cell(id), widthOf(id), CELL_PADDING)}</Fragment>
      ))}

      {/* Actions */}
      <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-3 py-2.5 text-right align-top group-hover:bg-slate-50">
        <RowActionsMenu label={order.orderNumber} actions={actions} />
      </td>
    </tr>
  );
}

/** How many slots the 2×2 grid has room for. Slots 5+ live in the order's
 *  additional-references JSON and are only surfaced here when one of them is
 *  required and empty — otherwise the cell would grow a third column on rows
 *  nobody asked anything of. */
const REF_GRID_SLOTS = 4;

/**
 * References cell — the order's four reference slots laid out as a 2×2 grid:
 * column 1 holds reference 1 (top) + reference 2 (bottom), column 2 holds
 * reference 3 + reference 4. Reference 1 reads as the bold headline; the
 * rest are muted. Empty slots keep their cell (a non-breaking space) so the
 * rows stay aligned. An order with no references at all shows a dash.
 *
 * <p>
 * A slot this CUSTOMER insists on (Party → Required References) that is empty
 * shows the field's name in amber instead of that blank — so the gap reads as
 * "this is expected here and missing" rather than as nothing at all, which is
 * exactly what an empty cell looks like today. Amber, not red: nothing refuses
 * the order, the customer simply wants it. Hard gates live in the workflow
 * policy, which applies to every customer alike.
 * </p>
 */
function ReferencesCell({
  order,
  customer,
  labels,
}: {
  order: Order;
  customer: Party | undefined;
  labels: string[];
}) {
  // Read through the shared extractor rather than the four columns: it also
  // pulls slots 5+ out of the additional-references JSON, which a customer may
  // legitimately require.
  const values = useMemo(() => extractOrderReferenceValues(order, REF_GRID_SLOTS), [order]);

  const missing = useMemo(() => {
    // Nothing to chase on an abandoned order — it will never be invoiced, so a
    // warning on it is noise with no action behind it.
    if (order.status === 'Cancelled') return [];
    return parseRequiredOrderReferenceSlots(customer?.tmsRequiredOrderReferencesJson).filter(
      (slot) => !(values[slot - 1] ?? '').trim(),
    );
  }, [order.status, customer?.tmsRequiredOrderReferencesJson, values]);

  const missingInGrid = missing.filter((slot) => slot <= REF_GRID_SLOTS);
  const missingBelow = missing.filter((slot) => slot > REF_GRID_SLOTS);

  const hasAnyValue = values.slice(0, REF_GRID_SLOTS).some((v) => v.trim());
  if (!hasAnyValue && missing.length === 0) {
    return <span className="text-[11px] italic text-slate-300">—</span>;
  }

  return (
    <div className="space-y-0.5">
      <div className="grid grid-flow-col grid-rows-2 gap-x-4 gap-y-0.5">
        {Array.from({ length: REF_GRID_SLOTS }, (_, idx) => idx + 1).map((slot) => (
          <RefItem
            key={slot}
            value={values[slot - 1]?.trim()}
            primary={slot === 1}
            missingLabel={
              missingInGrid.includes(slot)
                ? resolveOrderReferenceLabel(labels, slot - 1)
                : undefined
            }
          />
        ))}
      </div>
      {/* Required slots past the grid — named on one line rather than given a
          third column, which would misalign every other row. */}
      {missingBelow.length > 0 && (
        <span
          className="inline-flex items-center gap-1 text-[11px] italic text-amber-600"
          title="Required by this customer"
        >
          <AlertTriangle className="h-3 w-3 shrink-0" />
          {missingBelow.map((slot) => resolveOrderReferenceLabel(labels, slot - 1)).join(', ')}
        </span>
      )}
    </div>
  );
}

function RefItem({
  value,
  primary,
  missingLabel,
}: {
  value: string | undefined;
  primary?: boolean;
  /** Set when this slot is required by the customer and empty. */
  missingLabel?: string;
}) {
  if (!value && missingLabel) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] italic text-amber-600"
        title={`${missingLabel} — required by this customer`}
      >
        <AlertTriangle className="h-3 w-3 shrink-0" />
        {missingLabel}
      </span>
    );
  }
  return (
    <span
      className={primary ? 'text-xs font-semibold text-slate-800' : 'text-[11px] text-slate-600'}
    >
      {value || ' '}
    </span>
  );
}

// ── Table-header components ────────────────────────────────────────────────

function Th({
  children,
  align,
  width,
  resizer,
}: {
  children: React.ReactNode;
  align?: 'right' | 'center';
  /** Pixels when sized by hand. */
  width?: number;
  /** The drag strip on the right edge. */
  resizer?: React.ReactNode;
}) {
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th
      className={`relative overflow-hidden whitespace-nowrap px-3 py-2 font-semibold ${alignClass}`}
      style={columnWidthStyle(width)}
    >
      {children}
      {resizer}
    </th>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** "14 Oct, 09:32" in the browser's own locale and time zone. */
function formatCreated(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
}

function chipClass(active: boolean): string {
  return `rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors ${
    active
      ? 'border-slate-700 bg-slate-700 text-white'
      : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700'
  }`;
}
